import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  AppstoreOutlined,
  ReloadOutlined,
  SettingOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons'
import { Checkbox, Modal, Segmented } from 'antd'
import { Button, toast } from '@crosscode/gravity-components'
import { Page } from '@/shared/Page'
import { useStore } from '@/app/StoreProvider'
import { useAuth } from '@/app/AuthProvider'
import { PERMISSIONS } from '@/shared/permissions'
import * as cashApi from '@/features/cash/api'
import { CashFormDrawer } from '@/features/cash/CashFormDrawer'
import { CashGridCard } from '@/features/cash/CashGridCard'
import { CashKpi } from '@/features/cash/CashKpi'
import { CashManageBar } from '@/features/cash/CashManageBar'
import { CashManageTable } from '@/features/cash/CashManageTable'
import { CashTable } from '@/features/cash/CashTable'
import { copyIpToClipboard } from '@/features/cash/copyIp'
import type { CashListTab, CashTerminal, CashTerminalForm, CashTypeFilter, CashViewMode } from '@/features/cash/types'
import {
  filterByType,
  isTerminalProblematic,
  isUkm5,
  readCashViewMode,
  saveCashViewMode,
  sortTerminals,
  terminalLabel,
} from '@/features/cash/utils'
import styles from './CashPage.module.css'

export function CashPage() {
  const { storeId, selectedStore } = useStore()
  const { hasPermission } = useAuth()
  const navigate = useNavigate()
  const canEdit = hasPermission(PERMISSIONS.settingsEdit)

  const [view, setView] = useState<CashViewMode>(readCashViewMode)
  const [listTab, setListTab] = useState<CashListTab>('active')
  const [typeFilter, setTypeFilter] = useState<CashTypeFilter>('all')
  const [problemsOnly, setProblemsOnly] = useState(false)
  const [editMode, setEditMode] = useState(false)
  const [rows, setRows] = useState<CashTerminal[]>([])
  const [archivedRows, setArchivedRows] = useState<CashTerminal[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [formTerminal, setFormTerminal] = useState<CashTerminal | null | 'new'>(null)

  const setViewMode = (next: CashViewMode) => {
    setView(next)
    saveCashViewMode(next)
  }

  const exitEditMode = () => {
    setEditMode(false)
    setSelectedIds(new Set())
  }

  const loadGenRef = useRef(0)

  const applyEnrichment = useCallback(
    async (forStoreId: number, list: CashTerminal[], gen: number) => {
      const onlineIds = list.filter((t) => t.status === 'online').map((t) => t.id)
      if (!onlineIds.length) {
        if (gen === loadGenRef.current) {
          setRows((prev) => prev.map((t) => ({ ...t, kktFnPending: false })))
        }
        return
      }
      try {
        const enriched = await cashApi.enrichTerminalsMeta(forStoreId, onlineIds)
        if (gen !== loadGenRef.current) return
        const map = new Map(enriched.map((u) => [String(u.id), u]))
        setRows((prev) =>
          prev.map((t) => {
            const u = map.get(String(t.id))
            if (!u) {
              return {
                ...t,
                kktFnPending: false,
                kktStatus: t.kktStatus ?? '—',
                fnStatus: t.fnStatus ?? '—',
              }
            }
            return {
              ...t,
              kktFnPending: false,
              kktStatus: u.kktStatus ?? '—',
              fnStatus: u.fnStatus ?? '—',
              ...(u.status
                ? {
                    status: u.status,
                    ...(u.status === 'offline' ? { ms: 'unknown' } : {}),
                  }
                : {}),
            }
          }),
        )
      } catch {
        if (gen !== loadGenRef.current) return
        setRows((prev) => prev.map((t) => ({ ...t, kktFnPending: false })))
      }
    },
    [],
  )

  const loadActive = useCallback(
    async (fresh = false) => {
      if (!storeId) {
        setRows([])
        return
      }
      const gen = loadGenRef.current
      const list = await cashApi.fetchStoreTerminals(storeId, false, { fresh })
      if (gen !== loadGenRef.current) return
      const withPending = list.map((t) => ({
        ...t,
        kktFnPending: t.status === 'online',
      }))
      setRows(withPending)
      void applyEnrichment(Number(storeId), list, gen)
    },
    [storeId, applyEnrichment],
  )

  const loadArchived = useCallback(
    async (fresh = false) => {
      if (!storeId) {
        setArchivedRows([])
        return
      }
      const gen = loadGenRef.current
      const list = await cashApi.fetchStoreTerminals(storeId, true, { fresh })
      if (gen !== loadGenRef.current) return
      setArchivedRows(list)
    },
    [storeId],
  )

  const load = useCallback(
    async (opts?: { fresh?: boolean }) => {
      if (!storeId) {
        setRows([])
        setArchivedRows([])
        return
      }
      const fresh = Boolean(opts?.fresh)
      const gen = ++loadGenRef.current
      setLoading(true)
      setError(null)
      try {
        await Promise.all([loadActive(fresh), loadArchived(fresh)])
        if (gen !== loadGenRef.current) return
      } catch (err) {
        if (gen !== loadGenRef.current) return
        setError(err instanceof Error ? err.message : 'Ошибка загрузки касс')
        setRows([])
        setArchivedRows([])
      } finally {
        if (gen === loadGenRef.current) setLoading(false)
      }
    },
    [storeId, loadActive, loadArchived],
  )

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    setSelectedIds(new Set())
  }, [listTab, editMode, storeId])

  const sourceRows = listTab === 'active' ? rows : archivedRows

  const filteredRows = useMemo(() => {
    let list = filterByType(sourceRows, typeFilter)
    if (problemsOnly && listTab === 'active' && !editMode) {
      list = list.filter(isTerminalProblematic)
    }
    return sortTerminals(list)
  }, [sourceRows, typeFilter, problemsOnly, listTab, editMode])

  const ukm4Rows = useMemo(
    () => filteredRows.filter((t) => !isUkm5(t.type)),
    [filteredRows],
  )
  const ukm5Rows = useMemo(
    () => filteredRows.filter((t) => isUkm5(t.type)),
    [filteredRows],
  )

  const onlineCount = rows.filter((t) => t.status === 'online').length
  const offlineCount = rows.length - onlineCount
  const problemCount = rows.filter(isTerminalProblematic).length

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleAll = (ids: string[], checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      ids.forEach((id) => (checked ? next.add(id) : next.delete(id)))
      return next
    })
  }

  const openFull = (t: CashTerminal) => {
    navigate(`/cash/${t.id}`)
  }

  const handleSave = async (values: CashTerminalForm, terminalId?: string | number) => {
    if (terminalId != null) {
      await cashApi.updateTerminal(terminalId, values)
    } else {
      await cashApi.createTerminal(values)
    }
    await load()
  }

  const confirmArchive = (t: CashTerminal) => {
    Modal.confirm({
      title: 'Отправить в архив?',
      content: `${terminalLabel(t)} перестанет отображаться в мониторинге.`,
      okText: 'В архив',
      cancelText: 'Отмена',
      onOk: async () => {
        await cashApi.archiveTerminal(t.id)
        toast.success('Касса в архиве')
        await load()
      },
    })
  }

  const confirmRestore = (t: CashTerminal) => {
    Modal.confirm({
      title: 'Восстановить кассу?',
      content: `${terminalLabel(t)} снова появится в мониторинге.`,
      okText: 'Восстановить',
      onOk: async () => {
        await cashApi.restoreTerminal(t.id)
        toast.success('Касса восстановлена')
        await load()
      },
    })
  }

  const confirmDelete = (t: CashTerminal) => {
    Modal.confirm({
      title: 'Удалить навсегда?',
      content: `${terminalLabel(t)} будет удалена без возможности восстановления.`,
      okText: 'Удалить',
      okButtonProps: { danger: true },
      onOk: async () => {
        await cashApi.deleteTerminalPermanent(t.id)
        toast.success('Касса удалена')
        await load()
      },
    })
  }

  const runBulk = async (
    action: 'archive' | 'restore' | 'delete',
    ids: string[],
    labels: { title: string; ok: string; verb: (id: string) => Promise<void> },
  ) => {
    if (!ids.length) return
    Modal.confirm({
      title: labels.title,
      content: `Будет обработано касс: ${ids.length}`,
      okText: labels.ok,
      okButtonProps: action === 'delete' ? { danger: true } : undefined,
      onOk: async () => {
        await Promise.all(ids.map((id) => labels.verb(id)))
        toast.success('Готово')
        setSelectedIds(new Set())
        await load()
      },
    })
  }

  const manageHandlers = {
    archiveTab: listTab === 'archive',
    selectedIds,
    onToggleSelect: toggleSelect,
    onToggleAll: toggleAll,
    onEdit: (t: CashTerminal) => setFormTerminal(t),
    onArchive: confirmArchive,
    onRestore: confirmRestore,
    onDelete: confirmDelete,
    onCopyIp: copyIpToClipboard,
  }

  const showSplitByType =
    !editMode &&
    view === 'list' &&
    typeFilter === 'all' &&
    !problemsOnly &&
    listTab === 'active'

  const hasUkm4 = ukm4Rows.length > 0
  const hasUkm5 = ukm5Rows.length > 0
  const showDual = showSplitByType && hasUkm4 && hasUkm5

  const monitorContent =
    view === 'grid' ? (
      <div className={styles.grid}>
        {filteredRows.map((t) => (
          <CashGridCard
            key={String(t.id)}
            terminal={t}
            archiveTab={listTab === 'archive'}
            onOpen={openFull}
            onCopyIp={copyIpToClipboard}
          />
        ))}
      </div>
    ) : showDual ? (
      <div className={styles.dual}>
        <CashTable
          title="УКМ‑4"
          rows={ukm4Rows}
          archiveTab={false}
          onOpen={openFull}
          onCopyIp={copyIpToClipboard}
        />
        <CashTable
          title="УКМ‑5"
          rows={ukm5Rows}
          archiveTab={false}
          onOpen={openFull}
          onCopyIp={copyIpToClipboard}
        />
      </div>
    ) : showSplitByType && (hasUkm4 || hasUkm5) ? (
      <div className={styles.single}>
        <CashTable
          title={hasUkm4 ? 'УКМ‑4' : 'УКМ‑5'}
          rows={hasUkm4 ? ukm4Rows : ukm5Rows}
          archiveTab={false}
          onOpen={openFull}
          onCopyIp={copyIpToClipboard}
        />
      </div>
    ) : (
      <div className={styles.single}>
        <CashTable
          rows={filteredRows}
          archiveTab={listTab === 'archive'}
          onOpen={openFull}
          onCopyIp={copyIpToClipboard}
        />
      </div>
    )

  const tabContent = editMode ? (
    <div className={styles.single}>
      <CashManageTable rows={filteredRows} {...manageHandlers} />
    </div>
  ) : (
    monitorContent
  )

  return (
    <Page
      title="Кассы"
      count={
        storeId && !editMode && listTab === 'active' ? `${onlineCount}/${rows.length}` : undefined
      }
      subtitle={
        editMode
          ? `Управление · ${selectedStore?.name || 'магазин'}`
          : selectedStore?.name || 'Выберите магазин'
      }
      actions={
        <div className={styles.actionsBar}>
          {!editMode ? (
            <Segmented
              size="small"
              value={view}
              onChange={(v) => setViewMode(v as CashViewMode)}
              options={[
                { label: 'Список', value: 'list', icon: <UnorderedListOutlined /> },
                { label: 'Плитки', value: 'grid', icon: <AppstoreOutlined /> },
              ]}
            />
          ) : null}
          <Button
            icon={<ReloadOutlined />}
            onClick={() => void load({ fresh: true })}
            disabled={!storeId}
          >
            Обновить
          </Button>
          {canEdit && storeId ? (
            editMode ? (
              <Button type="primary" onClick={exitEditMode}>
                Готово
              </Button>
            ) : (
              <Button icon={<SettingOutlined />} onClick={() => setEditMode(true)}>
                Управление
              </Button>
            )
          ) : null}
        </div>
      }
      toolbar={
        storeId ? (
          <div className={styles.toolbarInner}>
            {!editMode && listTab === 'active' ? (
              <Checkbox checked={problemsOnly} onChange={(e) => setProblemsOnly(e.target.checked)}>
                Только проблемные
              </Checkbox>
            ) : null}
            <Segmented
              size="small"
              value={typeFilter}
              onChange={(v) => setTypeFilter(v as CashTypeFilter)}
              options={[
                { label: 'Все', value: 'all' },
                { label: 'УКМ‑4', value: 'ukm4' },
                { label: 'УКМ‑5', value: 'ukm5' },
              ]}
            />
            <Segmented
              size="small"
              className={styles.listTabSeg}
              value={listTab}
              onChange={(v) => setListTab(v as CashListTab)}
              options={[
                { label: `Активные ${rows.length}`, value: 'active' },
                { label: `Архив ${archivedRows.length}`, value: 'archive' },
              ]}
            />
          </div>
        ) : undefined
      }
      loading={loading}
      error={error}
      empty={!storeId}
      emptyText="Выберите магазин"
      emptyDescription="Магазин выбирается внизу боковой панели"
      onRetry={load}
    >
      {storeId && !editMode && listTab === 'active' ? (
        <CashKpi
          total={rows.length}
          online={onlineCount}
          offline={offlineCount}
          problems={problemCount}
          loading={loading}
          onProblemsClick={() => setProblemsOnly(true)}
        />
      ) : null}

      {editMode ? (
        <CashManageBar
          archiveTab={listTab === 'archive'}
          selectedCount={selectedIds.size}
          onAdd={() => setFormTerminal('new')}
          onBulkArchive={() =>
            runBulk('archive', [...selectedIds], {
              title: 'Отправить выбранные в архив?',
              ok: 'В архив',
              verb: (id) => cashApi.archiveTerminal(id),
            })
          }
          onBulkRestore={() =>
            runBulk('restore', [...selectedIds], {
              title: 'Восстановить выбранные?',
              ok: 'Восстановить',
              verb: (id) => cashApi.restoreTerminal(id),
            })
          }
          onBulkDelete={() =>
            runBulk('delete', [...selectedIds], {
              title: 'Удалить выбранные навсегда?',
              ok: 'Удалить',
              verb: (id) => cashApi.deleteTerminalPermanent(id),
            })
          }
          onDone={exitEditMode}
        />
      ) : null}

      {storeId ? tabContent : null}

      {storeId ? (
        <CashFormDrawer
          open={formTerminal !== null}
          storeId={Number(storeId)}
          storeName={selectedStore?.name}
          terminal={formTerminal === 'new' || formTerminal === null ? null : formTerminal}
          onClose={() => setFormTerminal(null)}
          onSubmit={handleSave}
        />
      ) : null}
    </Page>
  )
}
